import { useCookieConsent } from '../contexts/CookieContext';
import { useTranslation } from 'react-i18next';
import { SwitchToggle } from '../components/ui/SwitchToggle';
import { SEO } from '../components/shared/SEO';
import { useNavigate } from 'react-router-dom';
import { useLegalEntity } from '../hooks/useLegalEntity';

export const PrivacyPolicyPage = () => {
    const { consent, updateConsent, acceptAll, rejectAll } = useCookieConsent();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { entity, loading, fullAddress, taxLine } = useLegalEntity();

    const categories = [
        {
            id: 'essential',
            title: t('legal_pages.privacy_policy.cookie_management.essential_title'),
            description: t('legal_pages.privacy_policy.cookie_management.essential_desc'),
            disabled: true,
            value: true
        },
        {
            id: 'analytics',
            title: t('legal_pages.privacy_policy.cookie_management.analytics_title'),
            description: t('legal_pages.privacy_policy.cookie_management.analytics_desc'),
            disabled: false,
            value: consent.analytics
        },
        {
            id: 'marketing',
            title: t('legal_pages.privacy_policy.cookie_management.marketing_title'),
            description: t('legal_pages.privacy_policy.cookie_management.marketing_desc'),
            disabled: false,
            value: consent.marketing
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 font-sans text-slate-800">
            <SEO
                title={t('legal_pages.privacy_policy.title')}
                description="Informativa sulla privacy e gestione dei cookie di Deskbay."
                noindex={true}
            />
            <div className="max-w-4xl mx-auto bg-white p-8 sm:p-12 shadow-sm rounded-2xl">

                <h1 className="text-4xl font-bold mb-2">{t('legal_pages.privacy_policy.title')}</h1>
                <p className="text-slate-500 mb-8">
                    {t('legal_pages.privacy_policy.last_updated', { date: new Date().toLocaleDateString() })}
                </p>

                <div className="prose prose-slate max-w-none">

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.introduction.title')}
                        </h2>
                        <p>
                            {t('legal_pages.privacy_policy.introduction.text', {
                                company_name: entity.company_name,
                                website: entity.website
                            })}
                        </p>
                        <p className="mt-2">
                            {t('legal_pages.privacy_policy.introduction.commitment', {
                                company_name: entity.company_name
                            })}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.data_controller.title')}
                        </h2>
                        <p>
                            <strong>{entity.company_name}</strong><br />
                            {fullAddress}<br />
                            {taxLine}<br />
                            {t('legal_pages.privacy_policy.data_controller.email_label')} <a href={`mailto:${entity.email_info}`} className="text-sky-600 hover:underline">{entity.email_info}</a>
                            {entity.phone && (<><br />{t('legal_pages.privacy_policy.data_controller.phone_label')} {entity.phone}</>)}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.data_collected.title')}
                        </h2>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.privacy_policy.data_collected.voluntary_data')}</li>
                            <li>{t('legal_pages.privacy_policy.data_collected.usage_data')}</li>
                            <li>{t('legal_pages.privacy_policy.data_collected.notification_data')}</li>
                            <li>{t('legal_pages.privacy_policy.data_collected.restaurant_contact_profile')}</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.data_purpose.title')}
                        </h2>
                        <p>{t('legal_pages.privacy_policy.data_purpose.intro')}</p>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.privacy_policy.data_purpose.provide_service')}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.process_payments')}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.communicate')}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.analyze')}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.push_notifications')}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.marketing_comms', { company_name: entity.company_name })}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.restaurant_crm')}</li>
                            <li>{t('legal_pages.privacy_policy.data_purpose.legal_obligations')}</li>
                        </ul>
                    </section>

                    <section className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-8">
                        <h2 className="text-2xl font-semibold mb-6 text-slate-900">
                            {t('legal_pages.privacy_policy.cookie_management.title')}
                        </h2>
                        <p className="mb-6">
                            {t('legal_pages.privacy_policy.cookie_management.description')}
                        </p>

                        <div className="space-y-6">
                            {categories.map(cat => (
                                <div key={cat.id} className="flex items-start justify-between pb-4 border-b border-slate-200 last:border-0 last:pb-0">
                                    <div className="pr-4">
                                        <h3 className="font-bold text-lg text-slate-800">{cat.title}</h3>
                                        <p className="text-sm text-slate-600 mt-1">{cat.description}</p>
                                    </div>
                                    <div className="pt-1">
                                        <SwitchToggle
                                            enabled={!!cat.value}
                                            disabled={cat.disabled}
                                            onChange={() => updateConsent(cat.id as any, !cat.value)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-wrap gap-4">
                            <button
                                onClick={acceptAll}
                                className="px-6 py-2 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 transition"
                            >
                                {t('legal_pages.privacy_policy.cookie_management.accept_all')}
                            </button>
                            <button
                                onClick={rejectAll}
                                className="px-6 py-2 bg-white text-slate-600 border border-slate-300 rounded-lg font-semibold hover:bg-slate-50 transition"
                            >
                                {t('legal_pages.privacy_policy.cookie_management.reject_optional')}
                            </button>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.data_sharing.title')}
                        </h2>
                        <p>{t('legal_pages.privacy_policy.data_sharing.intro')}</p>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.privacy_policy.data_sharing.restaurant')}</li>
                            <li>{t('legal_pages.privacy_policy.data_sharing.infrastructure')}</li>
                            <li>{t('legal_pages.privacy_policy.data_sharing.payments')}</li>
                            <li dangerouslySetInnerHTML={{
                                __html: t('legal_pages.privacy_policy.data_sharing.communications', {
                                    fw360_privacy_link: '<a href="https://www.framework360.it/privacy-policy/" class="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy Framework360</a>'
                                })
                            }} />
                            <li dangerouslySetInnerHTML={{
                                __html: t('legal_pages.privacy_policy.data_sharing.push_notifications', {
                                    onesignal_gdpr_link: '<a href="https://onesignal.com/gdpr" class="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">OneSignal GDPR</a>',
                                    onesignal_privacy_link: '<a href="https://onesignal.com/privacy_policy" class="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">OneSignal Privacy</a>'
                                })
                            }} />
                            <li>{t('legal_pages.privacy_policy.data_sharing.analytics_marketing')}</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.user_rights.title')}
                        </h2>
                        <p>{t('legal_pages.privacy_policy.user_rights.intro')}</p>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.privacy_policy.user_rights.access')}</li>
                            <li>{t('legal_pages.privacy_policy.user_rights.rectify')}</li>
                            <li>{t('legal_pages.privacy_policy.user_rights.object')}</li>
                            <li>{t('legal_pages.privacy_policy.user_rights.portability')}</li>
                        </ul>
                        <p className="mt-4" dangerouslySetInnerHTML={{
                            __html: t('legal_pages.privacy_policy.user_rights.contact_text', {
                                email: `<a href="mailto:${entity.email_info}" class="text-sky-600 hover:underline">${entity.email_info}</a>`
                            })
                        }} />
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.deletion_procedure.title')}
                        </h2>
                        <p>{t('legal_pages.privacy_policy.deletion_procedure.intro')}</p>
                        <ol className="list-decimal pl-5 mt-2 space-y-3">
                            <li>
                                <strong>{t('legal_pages.privacy_policy.deletion_procedure.step1_title')}</strong> {t('legal_pages.privacy_policy.deletion_procedure.step1_desc')}
                            </li>
                            <li>
                                <strong>{t('legal_pages.privacy_policy.deletion_procedure.step2_title')}</strong> {t('legal_pages.privacy_policy.deletion_procedure.step2_desc')}
                            </li>
                            <li>
                                <strong>{t('legal_pages.privacy_policy.deletion_procedure.step3_title')}</strong> {t('legal_pages.privacy_policy.deletion_procedure.step3_desc')}
                            </li>
                        </ol>
                        <p className="mt-4">
                            {t('legal_pages.privacy_policy.deletion_procedure.retention_note')}
                        </p>
                        <p className="mt-4" dangerouslySetInnerHTML={{
                            __html: t('legal_pages.privacy_policy.deletion_procedure.auth_deletion_note', {
                                email: `<a href="mailto:${entity.email_info}" class="text-sky-600 hover:underline">${entity.email_info}</a>`
                            })
                        }} />
                    </section>

                    <section className="mb-8 border-t border-slate-200 pt-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.privacy_policy.contacts.title')}
                        </h2>
                        <p>
                            <strong>{entity.company_name}</strong><br />
                            {fullAddress}<br />
                            {taxLine}<br />
                            {t('legal_pages.privacy_policy.contacts.email_label')} <a href={`mailto:${entity.email_info}`} className="text-sky-600 hover:underline">{entity.email_info}</a>
                            {entity.phone && (<><br />{t('legal_pages.privacy_policy.contacts.phone_label')} {entity.phone}</>)}
                        </p>
                    </section>
                </div>
            </div>
            
            {/* Back Button */}
            <div className="fixed bottom-8 left-8 z-50">
                <button
                    onClick={() => navigate(-1)}
                    className="w-14 h-14 rounded-full shadow-[8px_8px_40px_rgba(0,0,0,0.4),4px_4px_20px_rgba(0,0,0,0.3)] bg-white/95 backdrop-blur-md border border-slate-200/50 hover:bg-slate-50 hover:shadow-[12px_12px_60px_rgba(0,0,0,0.5),6px_6px_30px_rgba(0,0,0,0.4)] hover:scale-105 transition-all duration-300 flex items-center justify-center group"
                >
                    <svg className="w-6 h-6 text-slate-700 group-hover:text-slate-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>
        </div>
    );
};
